// One recording as a row of /shares, as ShareRow is a share's: its kind as an
// icon, its title the link to its review; under it its turns, who recorded it
// when it is not mine, its assignment and, to its owner, the day it is kept
// until; then what it keeps (its screen, how much was masked, its score), what
// needs its owner (not uploaded, about to go), how its review stands where the
// relay says, and when it was recorded. Its owner's Edit and Delete are behind
// its ⋯ menu. What is in a title there — when exactly it was recorded, its
// events, when it goes, each kind masked — is a line of its own on a screen
// with no pointer to hover, a tablet's or a phone's.
import type { ReactNode } from "react";
import type { Recording } from "../api";
import type { Score } from "../protocol";
import { kindIcon, kindOf } from "../kinds";
import { notReached, notYetReviewing } from "../review";
import { t, when, whenShort, word, phrases } from "../i18n";
import { Link } from "./Nav";
import { RowMenu, type MenuItem } from "./RowMenu";
import { ScoreChip } from "./ScoreChip";
import { SelectBox } from "./Selection";

const day = 86_400_000;

/**
 * Days left before it is deleted, counted up: 1 on its last day, 0 once it
 * is due; null when its date does not read.
 */
export function daysLeft(r: Pick<Recording, "expires_at">, now = Date.now()): number | null {
  const left = Math.ceil((Date.parse(r.expires_at) - now) / day);
  return Number.isFinite(left) ? Math.max(left, 0) : null;
}

/** Days left before it is deleted, when that is a week or less. */
export function expiresSoon(r: Pick<Recording, "expires_at">, now = Date.now()): number | null {
  const left = daysLeft(r, now);
  return left !== null && left <= 7 ? left : null;
}

/** Whether any of these recordings has a review to say: someone reviews or was asked, or my own is done. */
export const reviewKnown = (rs: Recording[]) =>
  rs.some((r) => (r.role === "owner" ? (r.reviewers?.length ?? 0) + (r.review_requests?.length ?? 0) > 0 : !!r.done_at));

// How its review stands, as the relay's list says it (byj-xgt): to its
// owner, how many of those reviewing or asked are done, over its bar; to a
// reviewer, their own done, as the review page's Reviewers panel marks it.
function Review({ r }: { r: Recording }) {
  if (r.role === "owner") {
    const all = (r.reviewers ?? []).length + notYetReviewing(r).length;
    if (all === 0) return null;
    const done = (r.reviewers ?? []).filter((x) => x.done_at).length;
    return (
      <>
        <span>{t("shares:reviewProgress", { done, of: all })}</span>
        <span className="review-bar" aria-hidden="true">
          <span style={{ width: `${Math.round((done / all) * 100)}%` }} className={done === all ? "done" : undefined} />
        </span>
      </>
    );
  }
  return r.done_at ? (
    <span className="chip chip-ok" title={when(r.done_at)}>
      {t("recordings:reviewerDone", { when: whenShort(r.done_at) })}
    </span>
  ) : null;
}

// Those asked by email who have not come to review it yet (byj-xgt), as
// chips among its tags: invited, or not reached — the mail failed or the
// invite ran out, a person reviewing already whose mail failed among them —
// each person and state in its title.
function Asked({ r }: { r: Recording }) {
  const invited = notYetReviewing(r).filter((q) => q.state === "waiting");
  const unreached = notReached(r);
  const title = (qs: typeof invited) => qs.map((q) => `${q.email}: ${word("shares:requestState", q.state)}`).join("\n");
  return (
    <>
      {invited.length > 0 && (
        <span className="chip" title={title(invited)}>
          {t("shares:reviewInvited", { n: invited.length })}
        </span>
      )}
      {unreached.length > 0 && (
        <span className="chip chip-warn" title={title(unreached)}>
          {t("shares:reviewUnreached", { n: unreached.length })}
        </span>
      )}
    </>
  );
}

export function RecordingRow({ r, menu, select, review }: {
  r: Recording;
  menu: MenuItem[];
  /** While the list is being selected from, and this row may be picked. */
  select?: { picked: Set<string>; toggle: (id: string) => void };
  /** Whether the list has a review column (an empty one is width wasted). */
  review: boolean;
}) {
  const k = kindOf(r.source);
  const title = r.title || r.id;
  const owner = r.role === "owner";
  const soon = owner ? expiresSoon(r) : null;
  const sub = [
    !(r.turns[0] === 0 && r.turns[1] === 0) ? phrases.recordingTurns(r.turns[0], r.turns[1]) : null,
    !owner ? t("shares:recordedBy", { name: r.owner.name, role: word("common:role", r.role) ?? r.role }) : null,
    r.assignment ? r.assignment.title : null,
    // a class's, kept for it, yet with no assignment its instructor never sees it
    owner && r.class && !r.assignment ? t("recordings:notForInstructor") : null,
    // its owner's to keep or let go: the day, quietly, until it is near
    owner && soon === null ? t("shares:keptUntil", { when: whenShort(r.expires_at) }) : null,
  ].filter(Boolean);
  const masked = (r.masked ?? []).filter((m) => m.count > 0);
  const maskedTotal = masked.reduce((n, m) => n + m.count, 0);
  const maskedKinds = t("shares:maskedKinds", { kinds: masked.map((m) => `${m.kind} ${m.count}`).join(", ") });
  const recorded = t("recordings:recordingRecorded", { when: when(r.created_at) });
  const events = t("recordings:recordingEvents", { count: r.event_count });
  const expires = t("recordings:recordingExpires", { when: when(r.expires_at) });
  // what a pointer reads in the time's and the masked chip's titles, which a
  // touch screen cannot show: a line of its own there (.row-detail), less the
  // day it goes where the sub line already says it
  const detail = [recorded, events, owner && soon === null ? null : expires, maskedTotal > 0 ? maskedKinds : null];
  return (
    <li className="share-row rec-row">
      <span className="row-lead">{select && <SelectBox id={r.id} title={title} picked={select.picked} toggle={select.toggle} />}</span>
      <span className={`kind-box kind-${k}`} aria-hidden="true" title={word("shares:kindName", k) ?? k}>
        {kindIcon[k]}
      </span>
      <div className="row-main">
        <Link to={`/r/${encodeURIComponent(r.id)}`} className="row-title">
          {title}
        </Link>
        {sub.length > 0 && <div className="row-sub">{sub.join(" · ")}</div>}
      </div>
      <div className="row-detail">{detail.filter(Boolean).join(" · ")}</div>
      <div className="row-meta">
        <RecordingTags
          screen={r.screen}
          score={r.score}
          uploaded={r.uploaded}
          asked={owner && <Asked r={r} />}
          masked={
            maskedTotal > 0 && (
              <span className="chip" title={maskedKinds}>
                {t("shares:maskedCount", { n: maskedTotal })}
              </span>
            )
          }
          warn={soon !== null && <span className="chip chip-warn">{phrases.expiresSoon(soon)}</span>}
        />
        {review && (
          <span className="row-review">
            <Review r={r} />
          </span>
        )}
        <time className="row-time" dateTime={r.created_at} title={[recorded, events, expires].join(" · ")}>
          {whenShort(r.created_at)}
        </time>
      </div>
      <RowMenu label={t("shares:moreFor", { title })} items={menu} />
    </li>
  );
}

/** The class whose instructor reads a recording — one of an assignment — and so loses it with it, or none. */
export function instructorLoses(r: Pick<Recording, "assignment" | "class">): string | undefined {
  return r.assignment && r.class ? r.class.name : undefined;
}

/**
 * What a recording's row says of it in chips: its screen kept, `masked`,
 * its score, not uploaded, then `warn` — /shares' rows and the class's
 * (components/ClassRecordings) alike.
 */
export function RecordingTags({ screen, score, uploaded, masked, asked, warn }: {
  screen?: boolean;
  score?: Score;
  uploaded: boolean;
  masked?: ReactNode;
  /** who was asked to review it, its owner's (byj-xgt) */
  asked?: ReactNode;
  warn?: ReactNode;
}) {
  return (
    <span className="row-tags">
      {screen && <span className="chip">{t("shares:screenKept")}</span>}
      {masked}
      {score && <ScoreChip score={score} />}
      {asked}
      {!uploaded && <span className="chip chip-warn">{t("recordings:recordingNotUploaded")}</span>}
      {warn}
    </span>
  );
}
